import type {LocalDate} from '../domain/local-date.value-object';

const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MIN_MONTH = 1;
const MAX_MONTH = 12;
const MIN_DAY = 1;

const DAYS_IN_MONTH: Readonly<Record<number, number>> = {
  1: 31,
  2: 28,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
} as const;

const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

const daysInMonth = (year: number, month: number): number => {
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }

  return DAYS_IN_MONTH[month] ?? 0;
};

const pad2 = (value: number): string => value.toString().padStart(2, '0');

/** Browser-local calendar date as branded `LocalDate`. */
export const todayLocalDate = (now: Date = new Date()): LocalDate => {
  const y = now.getFullYear();
  const m = pad2(now.getMonth() + 1);
  const d = pad2(now.getDate());
  const value = `${y}-${m}-${d}`;
  if (!isLocalDate(value)) {
    throw new Error(`todayLocalDate produced an invalid LocalDate: ${value}`);
  }
  return value;
};

/** Parse `YYYY-MM-DD` into a local Date at noon (stable for DatePicker). */
export const parseLocalDateString = (value: string): Date => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
};

/** Format a Date to `YYYY-MM-DD` in the browser local calendar. */
export const toLocalDateString = (date: Date): LocalDate => {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  const value = `${y}-${m}-${d}`;
  if (!isLocalDate(value)) {
    throw new Error(`toLocalDateString produced an invalid LocalDate: ${value}`);
  }
  return value;
};

/**
 * Type guard for the {@link LocalDate} brand. Validates `YYYY-MM-DD`
 * strings and rejects impossible calendar dates like `2026-13-40`.
 */
export const isLocalDate = (value: string): value is LocalDate => {
  const match = LOCAL_DATE_PATTERN.exec(value);

  if (match === null) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  return (
    month >= MIN_MONTH && month <= MAX_MONTH && day >= MIN_DAY && day <= daysInMonth(year, month)
  );
};
